import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumb, Table } from '@fikasio/react-ui-components';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import EmptyTableRow from 'components/UI/EmptyTableRow';
import BasePage from 'components/UI/BasePage';
import SearchResultTypeIcon from 'components/UI/SearchResultTypeIcon';
import { useGetObjectivesQuery } from 'services/objectives/api';
import { FEATURES } from 'services/features/catalog';
import { useFeatureEnabled } from 'services/features/hooks';
import { useGetProjectsQuery } from 'services/projects/api';
import { useGetTasksQuery } from 'services/tasks/api';
import links from 'utils/links';
import { getSearchResultPath, getSearchResults, SEARCH_RESULT_LABEL_KEYS } from 'utils/search';
import { spaLinkProps } from 'utils/spaLink';

const SearchPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const query = new URLSearchParams(location.search).get('q') || '';
  const { enabled: objectivesEnabled } = useFeatureEnabled(FEATURES.objectives);
  const { enabled: tasksEnabled } = useFeatureEnabled(FEATURES.tasks);
  const { data: objectives } = useGetObjectivesQuery(undefined, { skip: !objectivesEnabled });
  const { data: projects } = useGetProjectsQuery();
  const { data } = useGetTasksQuery({}, { skip: !tasksEnabled });
  const visibleObjectives = objectivesEnabled ? objectives : undefined;
  const tasks = tasksEnabled ? data?.tasks : undefined;
  const results = useMemo(
    () => getSearchResults(query, { objectives: visibleObjectives, projects, tasks }),
    [projects, query, tasks, visibleObjectives],
  );

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname,
    });
  }, [location.pathname]);

  return (
    <>
      <Helmet>
        <title>{t('searchResults')}</title>
      </Helmet>
      <BasePage>
        <Breadcrumb
          items={[
            { ...spaLinkProps(links.paths.home, navigate), label: t('home') },
            { label: t('searchResults') },
          ]}
        />
        <h4>{t('searchResults')}</h4>
        <Table
          bordered
          hover
          responsive
        >
          <thead>
            <tr>
              <th style={{ width: 160 }}>{t('type')}</th>
              <th>{t('name')}</th>
            </tr>
          </thead>
          <tbody>
            {
              !results.length && (
                <EmptyTableRow
                  colSpan={2}
                  message={t('noSearchResults')}
                />
              )
            }
            {
              results.map(result => (
                <tr
                  key={`${result.type}-${result.id}`}
                  onClick={() => navigate(getSearchResultPath(result))}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <span
                      style={{
                        alignItems: 'center',
                        display: 'inline-flex',
                        gap: 8,
                      }}
                    >
                      <SearchResultTypeIcon type={result.type} />
                      {t(SEARCH_RESULT_LABEL_KEYS[result.type])}
                    </span>
                  </td>
                  <td>{result.name}</td>
                </tr>
              ))
            }
          </tbody>
        </Table>
      </BasePage>
    </>
  );
};

export default SearchPage;
